import { configureStore } from '@reduxjs/toolkit';
import messageSlice from '../redux/messageSlice';
import taskSlice from '../redux/taskSlice';
import dashboardSlice from '../redux/dashboardSlice';

// =============================================================================
// STATE OWNERSHIP — 4-TIER MODEL
// =============================================================================
//
// Before adding new state, pick the RIGHT tier. Using the wrong one causes
// prop drilling, unnecessary re-renders, or stale data.
//
// ┌──────────────────┬───────────────────────────────────────────────────────┐
// │ TIER             │ RULE                                                  │
// ├──────────────────┼───────────────────────────────────────────────────────┤
// │ 1. Server state  │ Data that comes from GraphQL belongs to Apollo cache. │
// │    (Apollo)      │ Use useQuery / useMutation. Do NOT copy server data   │
// │                  │ into useState or Redux — that creates two sources of  │
// │                  │ truth. Default fetchPolicy: 'no-cache' (see below).  │
// ├──────────────────┼───────────────────────────────────────────────────────┤
// │ 2. Global app    │ State that TWO OR MORE features need to read goes in  │
// │    state (Redux) │ Redux. This file registers the three slices:          │
// │                  │   message  → identity, conversations, chat UI flags   │
// │                  │   task     → active task, projects, task lists         │
// │                  │   dashboard → notifications                            │
// │                  │ Do NOT create a new slice without strong justification.│
// ├──────────────────┼───────────────────────────────────────────────────────┤
// │ 3. Feature state │ State shared across multiple components WITHIN ONE    │
// │    (Context)     │ feature tree. Lives in features/<name>/XxxContext.jsx │
// │                  │ and is wired at the route level in router.js.         │
// │                  │ Current feature contexts:                             │
// │                  │   features/auth/      → AuthContext, SignUpContext    │
// │                  │   features/chat/      → ConnectContext                │
// │                  │   features/tasks/     → TaskContext (src/context/)    │
// │                  │   features/dashboard/ → DashboardContext (src/context)│
// │                  │   features/files/     → UploadContext                 │
// │                  │   features/admin/     → AdminContext + sub-contexts   │
// ├──────────────────┼───────────────────────────────────────────────────────┤
// │ 4. Local UI      │ State owned by exactly one component: modal open,     │
// │    state         │ input value, tab index, animation flag.               │
// │    (useState)    │ If only ONE component reads it → useState here.       │
// │                  │ Do NOT lift to Context or Redux just to avoid props.  │
// └──────────────────┴───────────────────────────────────────────────────────┘
//
// FETCH POLICY RULES
// ------------------
// 'no-cache'          Default for most queries. XMPP delivers real-time
//                     updates directly; Apollo cache can't see them, so
//                     caching would serve stale data.
//
// 'cache-and-network' Use when the query result is stable (e.g. GET_ME,
//                     room list) and a fast initial paint matters more than
//                     guaranteed freshness. The background refetch keeps it
//                     current.
//
// 'cache-first'       Use ONLY for truly static reference data (e.g. module
//                     permissions list). Not appropriate for anything that
//                     changes during a session.
//
// ACTION NAMING CONVENTIONS
// -------------------------
// messageSlice actions → camelCase  (setUser, setConversations, setPopup)
// taskSlice actions    → snake_case (set_active_task, set_new_task)
// Do NOT mix the conventions. Match the slice's existing style.
// =============================================================================

export const store = configureStore({
    reducer: {
        message: messageSlice,
        task: taskSlice,
        dashboard: dashboardSlice,
    },
});
